import { AccountState } from '@/services/auth/types';
import type { AugmentedAccount } from '@/services/auth/types';
import { requiresCardCheckout } from '@/pages/checkout/utils/promo-checkout-transforms';

/** Also matches the `last` workspace sentinel. */
const WORKSPACE_URL = /^\/workspaces\/[^/]+(?:\/|$)/i;

/**
 * Workspace URL spaces the Journeys paywall does not cover.
 *
 * Pipelines and Design Studio are different products, with their own entitlements. `augment.ts` derives
 * expired messaging as "Journeys is active but the messaging feature is absent", which it documents as
 * a Journeys trial on a CDP plan. Accounts behind this paywall can therefore pay for Pipelines, and the
 * paywall must not remove it.
 */
const PAYWALL_EXEMPT_WORKSPACE_URLS = [
  /^\/workspaces\/[^/]+\/settings(?:[/?#]|$)/i,
  /^\/workspaces\/[^/]+\/pipelines(?:[/?#]|$)/i,
  /^\/workspaces\/[^/]+\/design-studio(?:[/?#]|$)/i,
  // Legacy aliases. This gate runs in the `_authenticated` layout, above the routes that rewrite these
  // URLs, so it must match them before they become the two spaces above.
  /^\/workspaces\/[^/]+\/journeys\/settings(?:[/?#]|$)/i,
  /^\/workspaces\/[^/]+\/parcel(?:[/?#]|$)/i,
];

/**
 * Journeys pages that moved out of the `/journeys/` URL segment stay covered. No guard reaches those
 * pages today, which is why this gate exists.
 *
 * Account-level `/settings/**` and `/trial-expired/journeys` are not workspace URLs, so they never
 * match. Both must stay reachable: the account pays under `/settings/billing`, and the paywall cannot
 * redirect to itself.
 */
function isTrialExpiryGuardedUrl(pathname: string): boolean {
  if (!WORKSPACE_URL.test(pathname)) {
    return false;
  }
  return !PAYWALL_EXEMPT_WORKSPACE_URLS.some((exempt) => exempt.test(pathname));
}

/**
 * Where an app-entry gate sends this account, or null if the account can enter the app.
 *
 * Keep the three conditions separate. They have different causes and different destinations, and an
 * account can satisfy one of them alone. The first match wins, so the order sets the priority.
 *
 * Hydra-owned routes do not start the embedded app, so the guards in that app never run for them.
 * Without these checks, an account reaches a migrated page that it must not see.
 *
 * This module has no React code, so tests can cover each branch without the app shell.
 */
export function appEntryGateDestination(
  account: AugmentedAccount | null,
  pathname: string,
  search = '',
): string | null {
  if (account == null) {
    return null;
  }

  const isMasq = account.is_masq === true;

  // A promo account that has not added a card yet has no provisioned Ordway customer, so it is
  // in the app without a way to be billed. Send it to checkout on every entry, so the block is
  // durable across navigation and reloads (SPE-3567). Masquerading staff are exempt — they are
  // not the ones who would pay.
  if (!isMasq && requiresCardCheckout(account)) {
    return '/checkout';
  }

  // A cancelled account has no app to use. Keep the query: the closure page reads `signupType`.
  if (!isMasq && account.account_state === AccountState.Canceled) {
    return `/account_canceled${search}`;
  }

  // Checked last. An account that owes a card, or that is cancelled, has a more specific destination.
  // This paywall would also hide the page where the account pays.
  //
  // A masquerade session gets no exemption here, unlike the two checks above. The guard in the
  // embedded app has no exemption either.
  //
  // Read `messaging.expired`, and not the wider `billing.trialExpired`. The embedded app sends accounts
  // to this paywall on `messaging.expired` alone, and the paywall route returns the others to the
  // dashboard, so the two flags must agree. `billing.trialExpired` also covers a trial past
  // `suspend_trial_at`, and the billing pages show those accounts a suspended-trial modal instead.
  if (account.planFeatures.messaging.expired === true && isTrialExpiryGuardedUrl(pathname)) {
    return '/trial-expired/journeys';
  }

  return null;
}
